import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { CustoProjetoTipo } from "@workflow/types";

import { APROVACAO_SUBMENU_IDS } from "@/components/menu/menu-catalog";
import { fluxoAprovacaoService } from "@/services/api/fluxo-aprovacao.service";
import { fluxoAprovacaoKeys } from "@/services/api/queries/fluxo-aprovacao.queries";
import { useAprovacaoNotifications } from "@/hooks/use-aprovacao-notifications";

/** Contagem de custo: mais volátil que orçamento → revalida em 30s e faz poll em 60s. */
const CONTAGEM_STALE = 30 * 1000;
const CONTAGEM_REFETCH = 60 * 1000;

/** Subtipo do motor genérico (custo) → id do submenu que recebe o badge. */
const SUBTIPO_PARA_SUBMENU: Record<string, string> = {
  [CustoProjetoTipo.DESPESA_EXTRAORDINARIA]: APROVACAO_SUBMENU_IDS.DESPESAS,
  [CustoProjetoTipo.COMISSAO]: APROVACAO_SUBMENU_IDS.COMISSOES,
};

/**
 * Resumo de aprovações pendentes por rota, para os badges do menu. Junta duas fontes:
 * o count de orçamentos (motor legado de versão) e a contagem agrupada por subtipo do
 * motor genérico (custo). Devolve um mapa `{ idDoSubmenu → quantidade }`; o pai
 * "Aprovações" soma os filhos no próprio componente.
 *
 * Cache no front (react-query): a contagem de custo compartilha o root
 * `['fluxo-aprovacao']`, então a invalidação pós-decisão (`useDecidirAprovacao`) a
 * atualiza na hora; o orçamento reusa o cache de `useAprovacaoNotifications`.
 */
export function useAprovacoesResumo({ enabled = true }: { enabled?: boolean } = {}) {
  const { notificationCount: orcamento, refreshNotifications } = useAprovacaoNotifications({
    enabled,
  });

  // Badge é puramente decorativo: NUNCA pode travar nem borbulhar erro para a UI.
  // - throwOnError: false → mesmo que o default global mude, jamais atinge um error boundary.
  // - retry: 1 → falha rápido e quieto (endpoint fora do ar não vira martelada de retries).
  // - placeholderData: keepPreviousData → um poll que falha mantém o último número bom,
  //   em vez de zerar os badges. O consumidor só lê `data`/`counts` — `error` é ignorado.
  const { data: contagemCusto, refetch } = useQuery({
    queryKey: fluxoAprovacaoKeys.minhasContagem(),
    queryFn: () => fluxoAprovacaoService.minhasContagem(),
    enabled,
    staleTime: CONTAGEM_STALE,
    refetchInterval: CONTAGEM_REFETCH,
    refetchOnWindowFocus: false,
    throwOnError: false,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const counts = useMemo(() => {
    const map: Record<string, number> = {
      [APROVACAO_SUBMENU_IDS.ORCAMENTOS]: orcamento,
    };
    for (const item of contagemCusto ?? []) {
      const submenuId = item.subtipo ? SUBTIPO_PARA_SUBMENU[item.subtipo] : undefined;
      if (submenuId) map[submenuId] = (map[submenuId] ?? 0) + item.quantidade;
    }
    return map;
  }, [orcamento, contagemCusto]);

  return {
    counts,
    refresh: () => {
      refreshNotifications();
      void refetch();
    },
  };
}
